// Orden y paginas de la lista de casos (Bandeja -> Casos): en la direccion (?orden=&dir=&pag=&tam=) para que los
// enlaces y las vistas abiertas lo conserven. El orden lo hace el motor (orderBy de GraphQL), asi aplica a todos los
// casos que cumplen los filtros, no solo a la pagina cargada. Twenty no tiene "offset": las paginas se piden con
// cursores (first/after, last/before) que se guardan por consulta (ver CursoresPagina).

export type CampoOrden = 'numero' | 'asunto' | 'grupo' | 'asignado' | 'estado' | 'fecha' | 'creado';
export type DirOrden = 'asc' | 'desc';
export type OrdenCasos = { campo: CampoOrden; dir: DirOrden };
export type PaginaCasos = OrdenCasos & { pag: number; tam: number; explicito: boolean };

export const TAMANOS = [50, 100, 300] as const;
export const TAM_DEFECTO = 100;
export const ORDEN_DEFECTO: OrdenCasos = { campo: 'fecha', dir: 'desc' };

// Campo del motor de cada columna. Cliente es una relacion (no se ordena en el servidor): la columna ordena por asunto.
// Asignado ordena por el id del miembro: junta los casos de cada persona, no es alfabetico.
export const CAMPO_MOTOR: Record<CampoOrden, string> = {
  numero: 'numero',
  asunto: 'name',
  grupo: 'grupo',
  asignado: 'asignadoId',
  estado: 'estado',
  fecha: 'ultimoMensaje',
  creado: 'createdAt',
};

// Al elegir una columna nueva: fechas y numero de mas nuevo a mas viejo; los textos de la A a la Z.
const DIR_NATURAL: Record<CampoOrden, DirOrden> = { numero: 'desc', asunto: 'asc', grupo: 'asc', asignado: 'asc', estado: 'asc', fecha: 'desc', creado: 'desc' };

const esCampo = (x: string | null): x is CampoOrden => !!x && x in CAMPO_MOTOR;

// Lee orden, pagina y tamano de la direccion. `tamGuardado`: el ultimo tamano elegido por la persona (si la direccion no
// trae uno). `explicito`: la persona eligio un orden (con busqueda, sin orden elegido se usa la relevancia).
export function leerPagina(sp: URLSearchParams, tamGuardado = TAM_DEFECTO): PaginaCasos {
  const o = sp.get('orden');
  const campo = esCampo(o) ? o : ORDEN_DEFECTO.campo;
  const d = sp.get('dir');
  const dir: DirOrden = d === 'asc' || d === 'desc' ? d : esCampo(o) ? DIR_NATURAL[campo] : ORDEN_DEFECTO.dir;
  const pag = Math.max(1, Math.floor(Number(sp.get('pag')) || 1));
  const t = Number(sp.get('tam'));
  const tam = (TAMANOS as readonly number[]).includes(t) ? t : (TAMANOS as readonly number[]).includes(tamGuardado) ? tamGuardado : TAM_DEFECTO;
  return { campo, dir, pag, tam, explicito: esCampo(o) };
}

// Escribe los cambios en la direccion. Los valores por defecto no se escriben; cambiar orden o tamano vuelve a la pagina 1.
export function escribirPagina(sp: URLSearchParams, c: Partial<{ campo: CampoOrden; dir: DirOrden; pag: number; tam: number }>) {
  const n = new URLSearchParams(sp);
  if (c.campo !== undefined || c.dir !== undefined) {
    const campo = c.campo ?? (esCampo(n.get('orden')) ? (n.get('orden') as CampoOrden) : ORDEN_DEFECTO.campo);
    const dir = c.dir ?? DIR_NATURAL[campo];
    if (campo === ORDEN_DEFECTO.campo && dir === ORDEN_DEFECTO.dir) {
      n.delete('orden');
      n.delete('dir');
    } else {
      n.set('orden', campo);
      n.set('dir', dir);
    }
    n.delete('pag');
  }
  if (c.tam !== undefined) {
    if (c.tam === TAM_DEFECTO) n.delete('tam');
    else n.set('tam', String(c.tam));
    n.delete('pag');
  }
  if (c.pag !== undefined) {
    if (c.pag <= 1) n.delete('pag');
    else n.set('pag', String(Math.floor(c.pag)));
  }
  return n;
}

// Clic en la cabecera de una columna: la misma columna invierte el sentido; otra empieza en su sentido natural.
export const siguienteOrden = (actual: OrdenCasos, campo: CampoOrden): OrdenCasos =>
  actual.campo === campo ? { campo, dir: actual.dir === 'asc' ? 'desc' : 'asc' } : { campo, dir: DIR_NATURAL[campo] };

// orderBy del motor. Los vacios siempre al final; despues la ultima actividad y el id para que el orden sea estable (los
// cursores necesitan un orden sin empates). `invertido`: el mismo orden al reves (para pedir la ultima pagina).
type DirMotor = 'AscNullsLast' | 'DescNullsLast' | 'AscNullsFirst' | 'DescNullsFirst';
export function ordenMotor(o: OrdenCasos, invertido = false): Record<string, DirMotor>[] {
  const dir = (asc: boolean): DirMotor => (invertido ? (asc ? 'DescNullsFirst' : 'AscNullsFirst') : asc ? 'AscNullsLast' : 'DescNullsLast');
  const out: Record<string, DirMotor>[] = [{ [CAMPO_MOTOR[o.campo]]: dir(o.dir === 'asc') }];
  if (o.campo !== 'fecha') out.push({ ultimoMensaje: dir(false) });
  out.push({ id: invertido ? 'DescNullsLast' : 'AscNullsFirst' });
  return out;
}

export const totalPaginas = (total: number, tam: number) => Math.max(1, Math.ceil(total / tam));

// "Casos 301–400 de 21.955": la primera y la ultima fila de la pagina.
export function rangoPagina(pag: number, tam: number, total: number) {
  if (!total) return { desde: 0, hasta: 0 };
  const desde = Math.min(total, (pag - 1) * tam + 1);
  return { desde, hasta: Math.min(total, pag * tam) };
}

// Botones del paginador: la primera, la ultima y dos a cada lado de la actual; '…' donde se saltan paginas.
export function paginasVisibles(actual: number, n: number): (number | '…')[] {
  const set = new Set<number>([1, n]);
  for (let i = actual - 2; i <= actual + 2; i++) if (i >= 1 && i <= n) set.add(i);
  const xs = [...set].sort((a, b) => a - b);
  const out: (number | '…')[] = [];
  xs.forEach((x, i) => {
    if (i && x - xs[i - 1] === 2) out.push(x - 1);
    else if (i && x - xs[i - 1] > 2) out.push('…');
    out.push(x);
  });
  return out;
}

/* ---------------- cursores por consulta ---------------- */

// Bordes de cada pagina ya vista: `inicio` es el cursor de su primera fila y `fin` el de la ultima. Con el `fin` de la
// pagina p-1 se pide la p hacia adelante (after); con el `inicio` de la p+1, hacia atras (before).
export type Bordes = { inicio: string | null; fin: string | null };
export class CursoresPagina {
  private paginas = new Map<number, Bordes>();
  // Cursor de cada fila suelta conocida (por posicion, desde 1), de los recorridos livianos.
  private filas = new Map<number, string>();

  // `cuantas`: filas que trajo la pagina (la ultima puede venir incompleta).
  guardar(pag: number, b: Bordes, tam: number, cuantas = tam) {
    this.paginas.set(pag, b);
    if (b.inicio) this.filas.set((pag - 1) * tam + 1, b.inicio);
    if (b.fin) this.filas.set((pag - 1) * tam + cuantas, b.fin);
  }
  guardarFila(posicion: number, cursor: string) {
    this.filas.set(posicion, cursor);
  }
  // Cursor de la fila en `posicion` (1 = primera), si se conoce.
  fila(posicion: number) {
    return this.filas.get(posicion) ?? null;
  }
  // Todas las filas conocidas [posicion, cursor] (para elegir desde donde recorrer menos).
  conocidas() {
    return [...this.filas];
  }
}

// Una cache de cursores por consulta (filtro + orden + tamano). Se limita para no crecer sin fin.
const caches = new Map<string, CursoresPagina>();
export function cursoresDe(firma: string) {
  let c = caches.get(firma);
  if (!c) {
    if (caches.size > 30) caches.delete(caches.keys().next().value as string);
    c = new CursoresPagina();
    caches.set(firma, c);
  }
  return c;
}

// Como llegar al cursor de la fila `objetivo` (la ultima de la pagina anterior) recorriendo lo menos posible: hacia
// adelante desde una fila conocida anterior (o desde el principio), hacia atras desde una posterior, o desde el final
// (el orden invertido) si se sabe el total. `n`: cuantas filas hay que pedir.
export type Recorrido =
  | { tipo: 'adelante'; desde: number; cursor: string | null; n: number }
  | { tipo: 'atras'; desde: number; cursor: string; n: number }
  | { tipo: 'final'; n: number };
export function planRecorrido(objetivo: number, total: number | null, conocidas: [number, string][]): Recorrido {
  let mejor: Recorrido = { tipo: 'adelante', desde: 0, cursor: null, n: objetivo };
  for (const [p, c] of conocidas) {
    if (p <= objetivo && objetivo - p < mejor.n) mejor = { tipo: 'adelante', desde: p, cursor: c, n: objetivo - p };
    else if (p > objetivo && p - objetivo < mejor.n) mejor = { tipo: 'atras', desde: p, cursor: c, n: p - objetivo };
  }
  if (total && objetivo <= total && total - objetivo + 1 < mejor.n) mejor = { tipo: 'final', n: total - objetivo + 1 };
  return mejor;
}
